// Trésorerie — tableau de flux de trésorerie
import { useState, useMemo } from "react";
import { ArrowLeftRight, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useCashflow } from "@/hooks/admin/useFinance";
import type { CashflowEntry } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const dateTo = now.toISOString().split("T")[0];
  return { dateFrom, dateTo };
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Cashflow() {
  const defaults = useMemo(() => getDefaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);

  const { data, isLoading, isError, refetch } = useCashflow(dateFrom, dateTo);

  const entries = data?.entries ?? [];

  const totalInflows = useMemo(
    () => entries.reduce((sum, e) => sum + e.inflows_fcfa, 0),
    [entries],
  );
  const totalOutflows = useMemo(
    () => entries.reduce((sum, e) => sum + e.outflows_fcfa, 0),
    [entries],
  );

  const columns: readonly ColumnDef<CashflowEntry>[] = useMemo(
    () => [
      {
        id: "date",
        header: "Date",
        cell: (e) => (
          <span className="text-sm font-medium text-stone-900">
            {formatDate(e.date)}
          </span>
        ),
      },
      {
        id: "inflows_fcfa",
        header: "Entrées",
        cell: (e) => (
          <span className="text-sm font-medium text-emerald-700">
            {formatFcfa(e.inflows_fcfa)}
          </span>
        ),
      },
      {
        id: "outflows_fcfa",
        header: "Sorties",
        cell: (e) => (
          <span className="text-sm font-medium text-red-700">
            {formatFcfa(e.outflows_fcfa)}
          </span>
        ),
      },
      {
        id: "net_fcfa",
        header: "Net",
        cell: (e) => (
          <div className="flex items-center gap-1">
            {e.net_fcfa >= 0 ? (
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5 text-red-600" />
            )}
            <span
              className={`text-sm font-semibold ${
                e.net_fcfa >= 0 ? "text-emerald-700" : "text-red-700"
              }`}
            >
              {formatFcfa(e.net_fcfa)}
            </span>
          </div>
        ),
      },
      {
        id: "balance_fcfa",
        header: "Solde cumulé",
        cell: (e) => (
          <span
            className={`text-sm font-semibold ${
              e.balance_fcfa >= 0 ? "text-stone-900" : "text-red-700"
            }`}
          >
            {formatFcfa(e.balance_fcfa)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <ArrowLeftRight className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Trésorerie
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${entries.length} jour(s) de données`}
              </p>
            </div>
          </div>

          {/* Date range */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-stone-600">Du :</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-stone-600">Au :</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Solde ouverture"
            value={data?.opening_balance_fcfa ?? 0}
            format="currency"
            icon={<ArrowLeftRight className="h-4 w-4" />}
          />
          <KPICard
            label="Total entrées"
            value={totalInflows}
            format="currency"
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <KPICard
            label="Total sorties"
            value={totalOutflows}
            format="currency"
            icon={<TrendingDown className="h-4 w-4" />}
          />
          <KPICard
            label="Solde clôture"
            value={data?.closing_balance_fcfa ?? 0}
            format="currency"
            icon={<ArrowLeftRight className="h-4 w-4" />}
          />
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les données de trésorerie.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && entries.length === 0 ? (
              <EmptyState
                icon={<ArrowLeftRight className="h-6 w-6" />}
                title="Aucune donnée de trésorerie"
                description="Sélectionnez une plage de dates pour afficher les flux."
              />
            ) : (
              <AdminDataTable
                data={entries}
                columns={columns}
                isLoading={isLoading}
                getRowId={(e) => e.date}
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
